/* The query cache, across a reload.
 *
 * A region shows its last successful read while the next one is in flight — that is the
 * whole point of `retainedQueryData` — but a reload emptied the cache, so the retention
 * path could never fire on a first paint and every screen started from a skeleton. The
 * snapshot is written to the browser's own storage and hydrated back before the first
 * render, so the console opens showing what it last knew and revalidates behind the paint.
 *
 * Four rules, and each one is a way the snapshot can lie:
 *
 *   - **The build id busts it.** A snapshot is shaped by the decoders that wrote it. A new
 *     console build may parse the same door differently, so a snapshot from another build
 *     is discarded, never migrated.
 *   - **Age busts it.** Past `maxAgeMs` the snapshot is not a head start, it is a
 *     yesterday's-numbers screen that happens to render fast.
 *   - **The confirmed principal owns it.** A read keyed to somebody else's principal is
 *     never written, and — because a global or project-keyed read carries no principal at
 *     all — the whole snapshot is dropped by `own` unless the session read confirms the
 *     person it was saved for. Identity itself is never persisted (`sessionQuery` carries
 *     `NOT_PERSISTED`), so that confirmation is always a door call.
 *   - **JSON is not the wire.** `Date`, `bigint`, and `Uint8Array` are tagged and rebuilt here;
 *     without that, every read carrying a `Timestamp` threw and every read carrying bytes
 *     restored as a lookalike object.
 *
 * A read may also opt out entirely (`meta: NOT_PERSISTED`) — a stream hydration snapshot
 * is only true at the cursor it was read at. A read too big to keep is left out on its own
 * (`MAX_QUERY_BYTES`), so one large answer costs only itself a cold start: before, one
 * 1.3 MB agent roster took the whole snapshot with it and every reload started cold.
 *
 * Hand-written rather than `@tanstack/query-persist-client-core`, because the four rules
 * above and the key filter are the parts that matter and none of them are that package's;
 * what is left is `dehydrate`/`hydrate`, which the query library already exports.
 */

import { dehydrate, hydrate } from "@tanstack/react-query";
import type {
  DehydratedState,
  Query,
  QueryClient,
} from "@tanstack/react-query";

const ONE_DAY_MS = 24 * 60 * 60_000;
/** Browser storage is a few megabytes for the whole origin and taking all of it would be
 * this cache evicting somebody else's key. A snapshot bigger than this is dropped rather
 * than written; the console still works, it just starts cold. */
const MAX_SNAPSHOT_BYTES = 1_000_000;
/** One read's share of that: a read bigger than this is left out of the snapshot alone,
 * and everything else is still kept. */
const MAX_QUERY_BYTES = 400_000;
/** A cache event fires per query transition. One write per second is enough for a
 * snapshot whose only reader is the next page load. */
const SAVE_THROTTLE_MS = 1_000;
// Older snapshots lost Date identity. Drop them even during a same-build dev reload.
const SNAPSHOT_FORMAT = 2;

/* Cached values whose identity JSON does not preserve.
 *
 * A generated message is a plain object, which is why a JSON round trip works on one at
 * all — but not entirely plain: a 64-bit field is a `bigint` and a bytes field is a
 * `Uint8Array`. `JSON.stringify` *throws* on the first (every `Timestamp.seconds` in the
 * tree, so most of the console's reads) and silently turns the second into `{"0":1,…}`,
 * which is worse: it restores as an object that every consumer treats as bytes.
 *
 * Decoded Dates also become strings. All three are tagged and rebuilt at this boundary. The
 * tags are keys no wire message can produce, and a tagged object is only revived when it
 * is the whole value — a message with a field *named* `$sxBigInt` would still be data. */
const BIGINT_TAG = "$sxBigInt";
const BYTES_TAG = "$sxBytes";
const DATE_TAG = "$sxDate";

const toBase64 = (bytes: Uint8Array): string => {
  let characters = "";
  for (const byte of bytes) characters += String.fromCharCode(byte);
  return btoa(characters);
};

const fromBase64 = (text: string): Uint8Array =>
  Uint8Array.from(atob(text), (character) => character.charCodeAt(0));

function tagged(
  this: Record<string, unknown>,
  key: string,
  value: unknown,
): unknown {
  // Date.toJSON runs before a replacer. Read the original holder to preserve the type,
  // without guessing whether an ordinary ISO-shaped string represents a Date.
  const original = this[key];
  if (original instanceof Date) return { [DATE_TAG]: original.toISOString() };
  if (typeof value === "bigint") return { [BIGINT_TAG]: value.toString() };
  if (value instanceof Uint8Array) return { [BYTES_TAG]: toBase64(value) };
  return value;
}

function untagged(_key: string, value: unknown): unknown {
  if (typeof value !== "object" || value === null) return value;
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record);
  if (keys.length !== 1) return value;
  if (keys[0] === DATE_TAG && typeof record[DATE_TAG] === "string") {
    const date = new Date(record[DATE_TAG]);
    if (!Number.isFinite(date.getTime()))
      throw new Error("Invalid cached date");
    return date;
  }
  if (keys[0] === BIGINT_TAG && typeof record[BIGINT_TAG] === "string") {
    return BigInt(record[BIGINT_TAG]);
  }
  if (keys[0] === BYTES_TAG && typeof record[BYTES_TAG] === "string") {
    return fromBase64(record[BYTES_TAG]);
  }
  return value;
}

/** A snapshot that could not be written. Carried as its own type because the only sane
 * answer is to forget the slot and stop: a value JSON cannot express will not start
 * being expressible a second later, and retrying every second is how the first cut of
 * this file turned one unserializable read into an unhandled rejection per second. */
export class SnapshotWriteError extends Error {
  constructor(readonly reason: unknown) {
    super("The query cache snapshot could not be serialized.");
    this.name = "SnapshotWriteError";
  }
}

/** One string-shaped place to keep the snapshot.
 *
 * Not `Storage`, and not a key: where the bytes live, whether the browser will accept
 * them, and how a refused write is reported are the consumer's questions — the console
 * answers all three at its own browser-persistence boundary. What is this package's is
 * only what may be written and when. */
export interface SnapshotSlot {
  read(): string | null;
  /** False when the browser refused: out of quota, or storage disabled. */
  write(value: string): boolean;
  clear(): void;
}

interface Envelope {
  readonly format: number;
  readonly buildId: string;
  readonly savedAt: number;
  readonly principalId: string | null;
  readonly state: DehydratedState;
}

export interface PersistOptions {
  readonly queryClient: QueryClient;
  readonly slot: SnapshotSlot;
  /** The exact bundle that wrote the snapshot. Any other build discards it. */
  readonly buildId: string;
  /** Who the cache belongs to right now; `undefined` until the session read lands. */
  readonly principalId: () => string | undefined;
  readonly maxAgeMs?: number;
  readonly now?: () => number;
}

export interface PersistedQueryCache {
  /** The principal the restored snapshot belonged to, or `undefined` when nothing was
   * restored. Read by `own` below, which is the one thing allowed to act on it. */
  readonly restoredPrincipalId: string | undefined;
  /** Say who the session actually confirmed, before anything reads the restored data.
   *
   * A snapshot is somebody's. Until the door has said who is at the keyboard now, every
   * value hydrated from disk is unattributed — and a device is shared: one person closes
   * the tab without signing out and the next person opens the console. Principal-keyed
   * reads are safe by construction (a different principal is a different key), but a
   * global or project-keyed read is not, so a snapshot whose owner is not the confirmed
   * principal — including nobody, when the session says signed out — is dropped rather
   * than merged. Returns whether it cleared the cache. */
  own(principalId: string | undefined): boolean;
  /** Write now instead of on the next throttle tick. */
  save(): void;
  /** Settle the slot and stop keeping it.
   *
   * The settling is not a courtesy: sign-out is `queryClient.clear()` followed by a
   * browser navigation, and between the two every mounted observer refetches. Without a
   * final write and an unsubscribe the throttled save fires a second later and puts a
   * pre-logout snapshot back on disk — the one moment the cache must be forgotten is the
   * one moment the old code would rewrite it. */
  stop(): void;
}

/** No snapshot, and nothing kept: what a suite's router carries, so a cache that is not
 * persisted is still a total value rather than an absent one. */
export const noPersistedQueryCache: PersistedQueryCache = {
  restoredPrincipalId: undefined,
  own: () => false,
  save: () => undefined,
  stop: () => undefined,
};

/** A key of the shape `doorQueryKeys.principal(...)` names its principal at index 2. */
function foreignPrincipal(
  key: readonly unknown[],
  principalId: string | undefined,
): boolean {
  return (
    key[1] === "principal" &&
    typeof key[2] === "string" &&
    key[2] !== principalId
  );
}

type CachedQuery = Query<unknown, Error, unknown, readonly unknown[]>;

/** Each read's serialized size, measured once per answer it holds. */
const measured = new WeakMap<CachedQuery, { readonly at: number; readonly bytes: number }>();

function bytesOf(query: CachedQuery): number {
  const seen = measured.get(query);
  if (seen !== undefined && seen.at === query.state.dataUpdatedAt) return seen.bytes;
  let bytes: number;
  try {
    bytes = JSON.stringify(query.state.data, tagged).length;
  } catch (cause) {
    throw new SnapshotWriteError(cause);
  }
  measured.set(query, { at: query.state.dataUpdatedAt, bytes });
  return bytes;
}

function persistable(
  query: CachedQuery,
  principalId: string | undefined,
): boolean {
  if ((query.meta as { persist?: unknown } | undefined)?.persist === false) {
    return false;
  }
  if (query.state.status !== "success" || query.state.data === undefined) {
    return false;
  }
  return !foreignPrincipal(query.queryKey, principalId) && bytesOf(query) <= MAX_QUERY_BYTES;
}

/** What a restore attempt produced. `hydrated` is not `principalId !== undefined`: a
 * snapshot saved before the session landed names no owner and is still on screen. */
interface Restored {
  readonly hydrated: boolean;
  readonly principalId: string | undefined;
}

const NOTHING_RESTORED: Restored = { hydrated: false, principalId: undefined };

function restore(
  options: PersistOptions,
  maxAgeMs: number,
  now: number,
): Restored {
  const stored = options.slot.read();
  if (stored === null) return NOTHING_RESTORED;
  let envelope: Envelope;
  try {
    envelope = JSON.parse(stored, untagged) as Envelope;
  } catch {
    options.slot.clear();
    return NOTHING_RESTORED;
  }
  const usable =
    envelope.format === SNAPSHOT_FORMAT &&
    envelope.buildId === options.buildId &&
    typeof envelope.savedAt === "number" &&
    now - envelope.savedAt <= maxAgeMs &&
    envelope.state !== null &&
    typeof envelope.state === "object";
  if (!usable) {
    options.slot.clear();
    return NOTHING_RESTORED;
  }
  hydrate(options.queryClient, envelope.state);
  return {
    hydrated: true,
    principalId: envelope.principalId ?? undefined,
  };
}

function serialize(envelope: Envelope | DehydratedState["queries"][number]): string {
  try {
    return JSON.stringify(envelope, tagged);
  } catch (cause) {
    throw new SnapshotWriteError(cause);
  }
}

/** Restore the last snapshot into `queryClient` and keep writing it back.
 *
 * Call this before the first render: hydration is synchronous, so the first paint is
 * already the last known screen rather than a skeleton that becomes one. */
export function persistQueryCache(
  options: PersistOptions,
): PersistedQueryCache {
  const now = options.now ?? Date.now;
  const maxAgeMs = options.maxAgeMs ?? ONE_DAY_MS;
  const restored = restore(options, maxAgeMs, now());

  let pending: ReturnType<typeof setTimeout> | undefined;
  /** True until `own` has been told who the session confirmed. */
  let unattributed = restored.hydrated;

  const write = (): void => {
    const principalId = options.principalId();
    const state = dehydrate(options.queryClient, {
      shouldDehydrateQuery: (query) => persistable(query, principalId),
      shouldDehydrateMutation: () => false,
    });
    // An empty cache is a signed-out or freshly cleared one. Removing the key rather
    // than writing an empty snapshot is what makes sign-out actually forget.
    if (state.queries.length === 0) {
      options.slot.clear();
      return;
    }
    const envelope: Envelope = {
      format: SNAPSHOT_FORMAT,
      buildId: options.buildId,
      savedAt: now(),
      principalId: principalId ?? null,
      state,
    };
    let serialized = serialize(envelope);
    if (serialized.length > MAX_SNAPSHOT_BYTES) {
      // Too much in all: the largest reads give way until the rest fits, so many
      // middling reads (an agent's settings, one per agent opened) cost themselves a cold
      // start rather than the whole snapshot.
      const sized = state.queries
        .map((query) => ({ query, bytes: serialize(query).length }))
        .sort((left, right) => left.bytes - right.bytes);
      let total = serialized.length;
      while (total > MAX_SNAPSHOT_BYTES && sized.length > 0) total -= sized.pop()!.bytes;
      if (sized.length === 0) {
        options.slot.clear();
        return;
      }
      serialized = serialize({ ...envelope, state: { ...state, queries: sized.map(({ query }) => query) } });
    }
    // A refused write is a cold start next time, which is the correct degradation; a
    // half-written slot is not, so drop it.
    if (!options.slot.write(serialized)) options.slot.clear();
  };

  /** Once true, nothing writes again for this page load. Every path to the slot goes
   * through `writeOrForget`, so this is the whole guarantee rather than a property of
   * whoever happens to still be subscribed. */
  let stopped = false;

  /** Write, or forget the slot and keep nothing more.
   *
   * Every write goes through here, including the throttled one, because that one runs
   * inside a timer where a throw is an unhandled rejection nobody sees. A value JSON
   * cannot express is permanent, so the answer is to forget the slot once rather than to
   * fail again on the next tick. */
  const writeOrForget = (): void => {
    if (stopped) return;
    try {
      write();
    } catch (error) {
      if (!(error instanceof SnapshotWriteError)) throw error;
      options.slot.clear();
      stopped = true;
      unsubscribe();
    }
  };

  const stop = (): void => {
    if (pending !== undefined) clearTimeout(pending);
    pending = undefined;
    if (stopped) return;
    unsubscribe();
    // Settle while still allowed — a cache that was just cleared forgets its slot here —
    // and only then close the door, so nothing that refetches afterwards writes it back.
    writeOrForget();
    stopped = true;
  };

  const save = (): void => {
    if (pending !== undefined) {
      clearTimeout(pending);
      pending = undefined;
    }
    writeOrForget();
  };

  const unsubscribe = options.queryClient.getQueryCache().subscribe(() => {
    // A cleared cache is written through immediately: `queryClient.clear()` is what
    // sign-out calls, and the browser leaves this page moments later.
    if (options.queryClient.getQueryCache().getAll().length === 0) {
      save();
      return;
    }
    pending ??= setTimeout(() => {
      pending = undefined;
      writeOrForget();
    }, SAVE_THROTTLE_MS);
  });

  return {
    restoredPrincipalId: restored.principalId,
    own: (principalId) => {
      if (!unattributed) return false;
      unattributed = false;
      if (principalId !== undefined && principalId === restored.principalId) {
        return false;
      }
      options.queryClient.clear();
      return true;
    },
    save,
    stop,
  };
}
